import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Minus, Plus, Trash2, Store, Truck, Loader2, Lock } from "lucide-react";
import { useStore } from "./StoreShell";
import { StoreHeader } from "../../components/market/StoreHeader";
import { mk, money } from "../../lib/marketApi";

const box = { background: "var(--mk-surface)", borderRadius: "var(--mk-radius)" };
const input = "w-full rounded-xl border border-slate-200 bg-white px-3.5 py-3 text-sm text-slate-900 outline-none focus:ring-2 focus:ring-[var(--mk-primary)]";

const MethodOption = ({ active, onClick, icon: Icon, title, sub, testid }) => (
  <button onClick={onClick} className="flex items-center gap-3 rounded-2xl border-2 p-3.5 text-left transition-colors" style={{ borderColor: active ? "var(--mk-primary)" : "rgba(0,0,0,.08)" }} data-testid={testid}>
    <Icon className="h-5 w-5 shrink-0" style={{ color: "var(--mk-primary)" }} />
    <span className="min-w-0">
      <span className="block text-sm font-semibold">{title}</span>
      <span className="block text-xs opacity-60">{sub}</span>
    </span>
  </button>
);

export default function Cart() {
  const { slug, store, cart, subtotal, setQty, buyer, setAuthOpen } = useStore();
  const sh = store.shipping || {};
  const cur = store.currency;
  const [method, setMethod] = useState(sh.delivery ? "delivery" : "pickup");
  const saved = buyer?.buyer?.address || {};
  const [addr, setAddr] = useState({ street: saved.street || "", colony: saved.colony || "", city: saved.city || "", zip: saved.zip || "", ref: saved.ref || "" });
  const [phone, setPhone] = useState(buyer?.buyer?.phone || "");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const free = sh.free_over > 0 && subtotal >= sh.free_over;
  const ship = method === "delivery" ? (free ? 0 : Number(sh.delivery_fee) || 0) : 0;
  const total = subtotal + ship;
  const setA = (k) => (e) => setAddr((a) => ({ ...a, [k]: e.target.value }));

  const pay = async () => {
    if (!buyer) return setAuthOpen(true);
    setBusy(true);
    setErr("");
    try {
      const d = await mk(`/s/${slug}/checkout`, {
        method: "POST",
        token: buyer.token,
        body: { items: cart.map((c) => ({ product_id: c.id, qty: c.qty })), method, address: method === "delivery" ? addr : null, phone, notes, origin_url: window.location.origin },
      });
      window.location.href = d.url;
    } catch (e) {
      setErr(e.message);
      setBusy(false);
    }
  };

  return (
    <>
      <StoreHeader />
      <main className="mx-auto max-w-5xl px-4 pb-16 pt-6">
        <h1 className="text-2xl font-bold">Tu carrito</h1>
        {!cart.length ? (
          <div className="mt-10 text-center" data-testid="cart-empty">
            <p className="opacity-60">Tu carrito está vacío.</p>
            <Link to={`/m/${slug}`} className="mt-4 inline-flex rounded-full px-6 py-3 text-sm font-semibold text-white" style={{ background: "var(--mk-primary)", color: "var(--mk-on-primary)" }}>
              Ver productos
            </Link>
          </div>
        ) : (
          <div className="mt-5 grid gap-5 lg:grid-cols-[1fr_380px]">
            <div className="space-y-5">
              <div className="divide-y divide-black/5 p-2" style={box} data-testid="cart-lines">
                {cart.map((c) => (
                  <div key={c.id} className="flex items-center gap-3 p-2" data-testid={`cart-line-${c.id}`}>
                    <img src={c.product.images[0]} alt="" className="h-16 w-16 shrink-0 rounded-xl object-cover" />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium">{c.product.name}</p>
                      <p className="text-sm font-bold">{money(c.product.price * c.qty, cur)}</p>
                    </div>
                    <div className="flex items-center rounded-full border border-black/10">
                      <button onClick={() => setQty(c.id, c.qty - 1)} className="p-2" data-testid={`cart-minus-${c.id}`}>{c.qty === 1 ? <Trash2 className="h-3.5 w-3.5" /> : <Minus className="h-3.5 w-3.5" />}</button>
                      <span className="w-5 text-center text-sm font-semibold">{c.qty}</span>
                      <button onClick={() => setQty(c.id, Math.min(c.product.stock, c.qty + 1))} className="p-2" data-testid={`cart-plus-${c.id}`}><Plus className="h-3.5 w-3.5" /></button>
                    </div>
                  </div>
                ))}
              </div>
              <div className="space-y-3 p-4" style={box}>
                <p className="font-semibold">Entrega</p>
                <div className="grid gap-2 sm:grid-cols-2">
                  {sh.delivery && (
                    <MethodOption active={method === "delivery"} onClick={() => setMethod("delivery")} icon={Truck} title="Envío a domicilio" sub={free ? "¡Gratis!" : `${money(sh.delivery_fee, cur)}${sh.free_over > 0 ? ` · gratis desde ${money(sh.free_over, cur)}` : ""}`} testid="cart-method-delivery" />
                  )}
                  {sh.pickup && (
                    <MethodOption active={method === "pickup"} onClick={() => setMethod("pickup")} icon={Store} title="Recoger en tienda" sub={sh.pickup_address || "Sin costo"} testid="cart-method-pickup" />
                  )}
                </div>
                {method === "delivery" && (
                  <div className="grid gap-2 sm:grid-cols-2">
                    <input className={`${input} sm:col-span-2`} placeholder="Calle y número" value={addr.street} onChange={setA("street")} data-testid="cart-addr-street" />
                    <input className={input} placeholder="Colonia" value={addr.colony} onChange={setA("colony")} data-testid="cart-addr-colony" />
                    <input className={input} placeholder="Ciudad" value={addr.city} onChange={setA("city")} data-testid="cart-addr-city" />
                    <input className={input} placeholder="Código postal" value={addr.zip} onChange={setA("zip")} data-testid="cart-addr-zip" />
                    <input className={input} placeholder="Referencias" value={addr.ref} onChange={setA("ref")} data-testid="cart-addr-ref" />
                  </div>
                )}
                <input className={input} placeholder="WhatsApp de contacto" value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="cart-phone" />
                <textarea className={input} rows={2} placeholder="Notas para el pedido (opcional)" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="cart-notes" />
              </div>
            </div>
            <aside className="h-fit space-y-3 p-5 lg:sticky lg:top-24" style={box} data-testid="cart-summary">
              <div className="flex justify-between text-sm"><span className="opacity-70">Subtotal</span><span data-testid="cart-subtotal">{money(subtotal, cur)}</span></div>
              <div className="flex justify-between text-sm"><span className="opacity-70">Envío</span><span data-testid="cart-shipping">{ship ? money(ship, cur) : "Gratis"}</span></div>
              <div className="flex justify-between border-t border-black/5 pt-3 text-lg font-bold"><span>Total</span><span data-testid="cart-total">{money(total, cur)}</span></div>
              {!store.payments_ready && <p className="rounded-xl bg-amber-50 p-3 text-xs text-amber-800" data-testid="cart-payments-off">Esta tienda aún no activa los pagos en línea.</p>}
              {err && <p className="text-sm text-red-600" data-testid="cart-error">{err}</p>}
              <button
                disabled={busy || !store.payments_ready}
                onClick={pay}
                className="flex w-full items-center justify-center gap-2 rounded-full py-4 text-sm font-semibold text-white shadow-lg transition-transform active:scale-95 disabled:opacity-50"
                style={{ background: "var(--mk-primary)", color: "var(--mk-on-primary)" }}
                data-testid="cart-pay-button"
              >
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />}
                {buyer ? `Pagar ${money(total, cur)}` : "Inicia sesión para pagar"}
              </button>
              <p className="text-center text-[11px] opacity-50">Pago seguro con tarjeta procesado por Stripe</p>
            </aside>
          </div>
        )}
      </main>
    </>
  );
}
